// AccountCreate.tsx
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useLocation, useSearchParams } from "react-router-dom";
import NewAccountForm from "./NewAccountForm.tsx";
import ContactInfoForm from "./ContactInfoForm.tsx";
import { HomeActions } from "../../../Services/_actions/home.action.js";

// Define ContactData type globally or here
export type ContactData = {
  name: string;
  designation: string;
  department: string;
  email: string;
  mobile: string;
  singlePointOfContact: string;
};

const emptyContact: ContactData = {
  name: "",
  designation: "",
  department: "",
  email: "",
  mobile: "",
  singlePointOfContact: "no",
};

export { emptyContact };

const AccountCreate: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const actions = HomeActions();

  const passedAccount = location.state?.account;
  const accountIdFromQuery = searchParams.get("accountId") || "";
  const accountId = passedAccount?.account_id || accountIdFromQuery || "";

  const [resolvedAccount, setResolvedAccount] = useState<any>(passedAccount || null);
  const [contacts, setContacts] = useState<ContactData[]>(
    passedAccount?.contacts?.length ? passedAccount.contacts : [emptyContact]
  );
  const [validateContacts, setValidateContacts] = useState<() => boolean>(() => () => true);

  const fetchAccountDetails = async (id: string) => {
    try {
      const response = await actions.getAccountDetailsById(id);
      if (response?.status === 200) {
        const accountData = response.data;
        setResolvedAccount(accountData);

        const initialContacts = accountData.contacts?.length
          ? accountData.contacts
          : [emptyContact];
        setContacts(initialContacts);
      } else {
        console.error("Failed to fetch account details:", response);
      }
    } catch (error) {
      console.error("Error fetching account details:", error);
    }
  };

  useEffect(() => {
    if (!accountId) {
      setResolvedAccount(null);
      setContacts([emptyContact]);
      return;
    }

    // Sidebar deep-link (query) always loads fresh; state navigation can reuse payload.
    if (accountIdFromQuery || !passedAccount) {
      fetchAccountDetails(accountId);
      return;
    }

    setResolvedAccount(passedAccount);
    setContacts(
      passedAccount.contacts?.length ? passedAccount.contacts : [emptyContact]
    );
  }, [accountId, accountIdFromQuery]);

  return (
    <Box sx={{ display: "flex", height: "85vh", gap: 2, p: 0 }}>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <NewAccountForm
          account={resolvedAccount}
          getContacts={() => contacts}
          refreshAccountData={() => fetchAccountDetails(accountId)}
          validateContacts={validateContacts}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <ContactInfoForm
          contacts={contacts}
          setContacts={setContacts}
          validateContactsExternally={(validateFn) =>
            setValidateContacts(() => validateFn)
          }
        />
      </Box>
    </Box>
  );
};

export default AccountCreate;
